import {
  Alert, Box, DialogActions, DialogContentText, FormControl, Stack, styled, Typography, DialogActionsProps, FormLabel,
  Accordion, AccordionSummary, AccordionDetails, Button, Menu, MenuItem, ListItemText,
} from "@mui/material";
import { useForm, Controller, Control } from 'react-hook-form';
import { MouseEvent, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Meeting, MeetingSlot, MeetingHoldEventError, MeetingUpdate, MeetingCreate,
  RecurringTimes, MeetingPriority, InvalidParticipantVotes, CabinetFeatures,
  AllOAuthGrantDetails, User, LeaderList, Calendar, MeetingRoom, PresetLocations, ZoomSettings,
  MeetingLeader, Leader, MeetingStatus, PrivateExternalParticipant, ConferenceContact
} from "../../../store";
import {
  ExcludedSlotInfo, currentMeetingFilter, getSlotDuration, getTemplateVars, TimeZone
} from "../../../utils/scheduleUtils";
import {
  CabDropdown, CabTextInput, CabTextTokenInput, CabSwitch, CabButton, CabIcon,
  CabTooltip, CabPanel, CabToggleChip,
} from "@CabComponents";
import { CabControlLabel } from "@CabComponents/CabControlLabel";
import { CabDurationPicker } from "@CabComponents/CabDurationPicker";
import TimeSlotChips from "./TimeSlotChips";
import CabSpinner from "@CabComponents/CabSpinner";
import colors from "../../../colors";
import { CabComponentProps } from "@CabComponents/cabStyled";
import { DependentController, useAutoSubmit } from "../../../utils/formUtils";
import { CabModal } from "@CabComponents/CabModal";
import { useDeepCompareEffect } from "react-use";
import ExecutiveHoldAlert from "./ExecutiveHoldAlert";
import QuestionChips from "./QuestionChips";
import QuestionModal from "./QuestionModal";
import PollUpdateModal from '../PollUpdateModal/PollUpdateModal';
import {
  transformMarkupToSimpleTemplate, transformMarkupToText, transformSimpleTemplateToMarkup,
} from "@CabComponents/CabTextTokenInput";
import { CabTextTokenDisplay } from "@CabComponents/CabTextTokenDisplay";
import { ACCEPTED_MIME_EXTS, MEETING_TYPE, PAGE_URL, PROVIDER } from "../../../constants";
import RecurringRangeModal from "../ReusableBooking/RecurringRangeModal";
import RecurringDayChips from "./RecurringDayChips";
import ExecutiveHoldControl from "./ExecutiveHoldControl";
import EditMeetingInfoModal from "./EditMeetingInfoModal";
import { checkForMicrosoftGrant } from "../../../utils/authUtils";
import CalendarAndLocation from "./CalendarAndLocation";
import MeetingParticipants from "./MeetingParticipants";
import EditParticipantsModal from "../EditParticipantsModal";
import {
  IoChevronDown, IoInformationCircleOutline, IoHelpCircleOutline, IoAddOutline, IoCalendarOutline, IoPencilOutline,
  IoDocumentOutline, IoBookmarkOutline, IoLockClosed, IoRepeatOutline, IoCheckboxOutline, IoPeopleOutline,
  IoCalendarNumberOutline, IoDocumentTextOutline,
} from "react-icons/io5";
import { IoMdCalendar } from "react-icons/io";
import { IconType } from "react-icons";
import { AnalyticsEventColor } from "../../../store/cabinetApi/generated/analytics";
import {
  SchedulingPreferencesList, NewMeetingDefaultsOutput, MeetingTemplate,
} from "../../../store/cabinetApi/generated/scheduling";
import { DateTime } from "luxon";
import { scheduling } from "../../../store/cabinetApi/scheduling";
import { router } from "../../../router";


const formatDuration = (val: number) => `${Math.floor(val / 60)}:${(val % 60).toString().padStart(2, '0')}`;

type MeetingSettingsSection = 'info' | 'holds' | 'notes' | 'questions' | 'files' | 'times' | 'participants';
type ExpandedMeetingSettingsSection = MeetingSettingsSection | null;

const StyledCabDurationPicker = styled(CabDurationPicker, { label: "StyledCabDurationPicker" })({
  marginTop: "8px"
});

// const meetingPanelGray = '#FAFCFC';
const meetingPanelGray = colors.calmGrey50;

export interface MeetingSettingsProps {
  meeting: (Partial<Meeting> & { id: Meeting['id'] }) | (Partial<Meeting> & { id: -1, book_on_create?: boolean });
  meetingSlots: MeetingSlot[];
  leaderMap: {[key: string]: Leader}
  holdCalendarOptions: { id: number; calendar_id: string; name: string, can_edit?: boolean }[];
  calendarTimezone?: TimeZone;
  secondaryTimezones?: (TimeZone | undefined)[];
  showHolds?: boolean;
  onDeleteSlots: (slots: MeetingSlot[]) => void;
  onRemoveLeader: (meetingLeaderId: MeetingLeader['id']) => void;
  onShowErrors: (show: boolean) => void;
  meetingErrors: MeetingHoldEventError[];
  onSubmit: (meeting: MeetingUpdate, files?: File[]) => Promise<void>;
  onCreateNewMeeting: (meeting: MeetingCreate, files?: File[],
    participants?: PrivateExternalParticipant[], meetingCreateLeaders?: MeetingLeader[]) => Promise<void>;
  onShare: () => void;
  onCancel: () => void;
  openUpgradeModal: () => void;
  loading?: boolean;
  open: boolean;
  schedulingPrefs: SchedulingPreferencesList;
  isOwner: boolean;
  updateExecHoldNoticePref: (value: boolean) => void;
  slotsAreRecalculating: boolean;
  pollUpdateOpen: boolean;
  pollUpdateHandlerAccept: () => void;
  pollUpdateHandlerReject: () => void;
  invalidMeetingSlots: InvalidParticipantVotes;
  dragTimeslotsText: string;
  handleSaveRecurringTimes: (recurringTimes?: RecurringTimes) => void;
  onExcludedSlotsCreated: (slots: ExcludedSlotInfo[]) => void;
  onEditSlot: (eventId: string, start: Date, end: Date, isExcluded: boolean) => void;
  deleteMeetingFile: (id: number, meetingId: number) => void;
  features?: CabinetFeatures;
  oauth_grant_details?: AllOAuthGrantDetails;
  user: User | null | undefined;
  zoomSettings: {
    [settingsId: string]: ZoomSettings;
  };
  onAddZoom: () => void;
  leaders: LeaderList;
  leaderCalendarOptions: Calendar[];
  calendars: Calendar[];
  meetingRooms: { [id: string]: MeetingRoom };
  presetLocations: PresetLocations;
  handleDeleteAttendee: (id: number) => void;
  meetingType: keyof typeof MEETING_TYPE;
  onCalendarTimezoneSelected?: (timezone: TimeZone) => void;
  meetingDefaults?: NewMeetingDefaultsOutput;
}

export interface MeetingSettingFormInput {
  meetingName: string;
  internalMeetingLabel?: string;
  description: string;
  notes: string;
  duration: number;
  attendees: number;
  executiveHold: boolean;
  executiveHoldCalendars: number[];
  selectExactTimes: boolean;
  preventDoubleBookings: boolean;
  allowAddParticipants: boolean;
  startDelay: number;
  files: File[] | null;
  recurringTimes: RecurringTimes | null | undefined;
  selectedCalendar?: number;
  selectedColor?: AnalyticsEventColor;
  privateVisibility: boolean;
  bufferStartMinutes: number;
  bufferEndMinutes: number;
  allDaySlots: boolean;
}

export interface LocationsFormInput {
  locations?: ({
    conference?: {
      provider: number | null;
      leader: number | null;
      settings?: ConferenceSettings;
    },
    locationText?: string;
    room?: number;
    presetLocation?: number;
    none?: boolean;
  })[];
}

export interface ZoomConferenceSettings {
  provider: 3;
  alternativeHosts?: ConferenceContact[];
  autoRecording?: "none" | "local" | "cloud";
  joinBeforeHost?: boolean;
}

export interface GoogleMeetConferenceSettings {
  provider: 1;
}

export interface MSTeamsConferenceSettings {
  provider: 2;
}

export type ConferenceSettings =
  | ZoomConferenceSettings
  | GoogleMeetConferenceSettings
  | MSTeamsConferenceSettings;

const getDefaultTitle = (leaders: Leader[], isReusable: boolean): string => {
  if (isReusable && leaders.length > 0) {
    let title = "";
    if (leaders.length > 1) {
      title = leaders.map(leader => leader.first_name).join(", ");
    } else if (leaders.length === 1) {
      title = leaders[0].first_name + " " + leaders[0].last_name;
    }
    title += " <> {{attendee}}";
    return title;
  }
  return "";
};

const uploadFilesText = `Files will be attached to the event automatically when using the booking link.`;

export const transformLocations = (locations: LocationsFormInput['locations']) => {
  const [conferenceLocation] = locations?.filter(loc => loc?.conference) || [];
  const conferenceProvider: number | null | undefined = conferenceLocation?.conference?.provider || null;
  const conferenceLeader: number | null | undefined = conferenceLocation?.conference?.leader || null;

  const submitLocations = locations?.filter(loc => loc?.locationText != null)
    .map(loc => loc?.locationText ? transformMarkupToSimpleTemplate(loc.locationText) : '') as string[];
  const rooms = locations?.filter(loc => loc?.room != null && loc.room !== -1)
    .map(loc => loc?.room) as number[];
  const submitPresetLocations = locations?.filter(loc => loc?.presetLocation != null)
    .map(loc => loc?.presetLocation) as number[];

  const conferenceSettings = conferenceLocation?.conference?.settings;
  const zoomConferenceSettings = conferenceProvider === PROVIDER.ZOOM.id
    && conferenceSettings?.provider === 3
    ? {
      alternate_hosts: conferenceSettings.alternativeHosts ?? [],
      auto_recording: conferenceSettings.autoRecording ?? "none",
      join_before_host: conferenceSettings.joinBeforeHost ?? false,
    }
    : null;

  return {
    conference_provider: conferenceProvider,
    conference_leader: conferenceLeader,
    locations: submitLocations,
    rooms,
    location_presets: submitPresetLocations,
    zoom_conference_settings: zoomConferenceSettings,
  };
};

const removeDeletedQuestionTokensFromMarkup = (
  text: string | undefined,
  validQuestionIds: Set<number>,
): string => {
  if (!text) return '';

  return text.replace(/\{\{(?:answer|question)_(-?\d+)\}\}(?:\([^)]*\))?/g, (match, questionIdStr) => (
    validQuestionIds.has(Number(questionIdStr)) ? match : ''
  ));
};


const MeetingSettings = ({ meeting, meetingSlots, holdCalendarOptions, calendarTimezone,
  secondaryTimezones, onDeleteSlots, onShowErrors, meetingErrors, showHolds, onSubmit, loading, onShare, open,
  schedulingPrefs, isOwner, updateExecHoldNoticePref, onCreateNewMeeting, onCancel, openUpgradeModal,
  slotsAreRecalculating, onRemoveLeader, pollUpdateOpen, pollUpdateHandlerAccept, pollUpdateHandlerReject,
  invalidMeetingSlots, dragTimeslotsText, handleSaveRecurringTimes, onEditSlot, onExcludedSlotsCreated,
  deleteMeetingFile, features, oauth_grant_details, user, zoomSettings, leaders, calendars, meetingRooms,
  presetLocations, handleDeleteAttendee, leaderMap, leaderCalendarOptions, meetingType, onAddZoom,
  onCalendarTimezoneSelected, meetingDefaults
}: MeetingSettingsProps): ReactElement | null => {
  const [isPoll, setIsPoll] = useState(meeting.is_poll);
  const [isReusable, setIsReusable] = useState(meeting.is_reusable);
  const [isDirectEvent, setIsDirectEvent] = useState(
    meeting.id === -1 && 'book_on_create' in meeting ? meeting.book_on_create : false
  );
  const [openEditModal, setOpenEditModal] = useState(false);
  const [showNoSlotsWarning, setShowNoSlotsWarning] = useState(false);
  const [showSlotDurationWarning, setShowSlotDurationWarning] = useState(false);
  const [executiveHoldAlertOpen, setExecutiveHoldAlertOpen] = useState<number | null>(null);
  const [openQuestionModal, setOpenQuestionModal] = useState(false);
  const [forceRequired, setForceRequired] = useState(false);
  const [disableSelectExactTimes, setDisableSelectExactTimes] = useState(false);
  const [openSlotChangeModal, setOpenSlotChangeModal] = useState(false);
  const [recurringRangeModalOpen, setRecurringRangeModalOpen] = useState(false);
  const [editParticipantsModalOpen, setEditParticipantsModalOpen] = useState(false);
  const [
    expandedSection, setExpandedSection
  ] = useState<ExpandedMeetingSettingsSection>(null);
  const [maxFilesExceeded, setMaxFilesExceeded] = useState(false);

  const [fillTemplateAnchor, setFillTemplateAnchor] = useState<HTMLElement | null>(null);
  const { data: meetingTemplatesResponse, isFetching: meetingTemplatesFetching } =
    scheduling.useAppSchedulingMeetingTemplatesListQuery();
  const fillTemplateMenuItems = useMemo(() => (
    [...(meetingTemplatesResponse || [])]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  ), [meetingTemplatesResponse]);

  const selectedLeaderIds = useMemo(() => meeting.leader_info?.map(sl => sl.id) || [], [meeting.leader_info]);

  const meetingLeaders = useMemo(() => meeting.leader_info?.map(l => ({
    ...l, id: l.meeting_leader_id, meeting: meeting.id, leader: l.id,
    required: l.required != null ? l.required : true,
    prevent_conflicts: l.prevent_conflicts != null ? l.prevent_conflicts : true,
    should_invite: l.should_invite != null ? l.should_invite : true,
    view_calendar: l.view_calendar != null ? l.view_calendar : true,
    is_fetchable: Object.values(calendars).filter(c => c.leaders.includes(l.id)).some(c => c.calendar_access_id),
  })) || [], [meeting.leader_info, meeting.id, calendars]);

  const showTokenInputs = !isPoll;
  const isSavedMeeting = meeting.id !== -1;

  const holdsScrollRef = useRef<HTMLDivElement>(null);
  const notesScrollRef = useRef<HTMLDivElement>(null);
  const questionsScrollRef = useRef<HTMLDivElement>(null);
  const filesScrollRef = useRef<HTMLDivElement>(null);
  const sectionScrollRefs: Partial<Record<MeetingSettingsSection, typeof holdsScrollRef>> = {
    holds: holdsScrollRef,
    notes: notesScrollRef,
    questions: questionsScrollRef,
    files: filesScrollRef,
  };

  const hasMicrosoftGrant = useMemo(() => (
    oauth_grant_details && checkForMicrosoftGrant(oauth_grant_details)
  ), [oauth_grant_details]);

  const meetingParticipants = useMemo(() => Object.values(meeting.participants || {}), [meeting.participants]);

  const templateVars = useMemo(() => {
    return getTemplateVars(meeting.questions);
  }, [meeting.questions]);

  const templateNameLookup = useMemo(() => (
    Object.entries(templateVars)
      .map(([k, v]) => ({ [k]: v.replaceText }))
      .reduce((a, b) => ({ ...a, ...b }), {})
  ), [templateVars]);

  const locDefaultValues: LocationsFormInput = useMemo(() => {
    const locations: NonNullable<LocationsFormInput['locations']> = [
      ...(meeting.conference_provider && meeting.conference_provider !== PROVIDER.NO_PROVIDER.id
        ? [{
          conference: {
            provider: meeting.conference_provider,
            leader: meeting.conference_leader || null,
          },
        }]
        : meetingDefaults?.conference_provider?.provider ? [{ conference: {
          provider: meetingDefaults?.conference_provider?.provider || null,
          leader: meetingDefaults?.conference_leader?.id || null }
        }]
          : []
      ),
      ...(meeting.rooms || []).map(roomId => ({ room: roomId })),
      ...(meeting.location_presets || []).map(presetLoc => ({ presetLocation: presetLoc })),
      ...(meeting.locations || []).map(loc => ({
        locationText: transformSimpleTemplateToMarkup(loc, templateNameLookup),
      })),
    ];

    if (locations.length === 0) {
      locations.push({ none: true });
    }

    return { locations };
  }, [meeting.conference_leader, meeting.conference_provider, meeting.locations,
    meeting.rooms, meeting.location_presets, templateNameLookup, meetingDefaults?.conference_provider?.provider,
    meetingDefaults?.conference_leader?.id]);

  const defaultValues: MeetingSettingFormInput = useMemo(() => {
    const defaultTitle = getDefaultTitle(
      selectedLeaderIds.map((leaderId) => leaderMap[leaderId]).filter(l => !!l), isReusable || false
    );

    // For Calendar Event creation (isDirectEvent), default to the first available calendar from selected leaders
    let defaultCalendar = meeting.booking_calendar;
    if (isDirectEvent && !meeting.booking_calendar && selectedLeaderIds.length > 0) {
      // Find the first calendar that belongs to one of the selected leaders
      const leaderCalendar = leaderCalendarOptions.find(cal =>
        selectedLeaderIds.some(leaderId => cal.leaders.includes(leaderId))
      );
      if (leaderCalendar) {
        defaultCalendar = leaderCalendar.id;
      }
    }

    return {
      meetingName: showTokenInputs
        ? transformSimpleTemplateToMarkup(meeting.title || defaultTitle, templateNameLookup)
        : meeting.title || defaultTitle,
      internalMeetingLabel: meeting.internal_label || '',
      description: showTokenInputs
        ? transformSimpleTemplateToMarkup(meeting.description || '', templateNameLookup)
        : meeting.description || '',
      notes: meeting.notes || '',
      duration: meeting.duration_minutes || schedulingPrefs.user_prefs?.default_duration_minutes || 30,
      attendees: meeting.num_expected_participants || 1,
      executiveHold: (meeting.hold_calendars && meeting.hold_calendars.length > 0) || false,
      executiveHoldCalendars: meeting.hold_calendars || [],
      selectExactTimes: meeting.auto_merge_slots != null ? !meeting.auto_merge_slots : false,
      preventDoubleBookings: isPoll ? meeting.prevent_conflict || false :
        meeting.prevent_conflict !== undefined ? meeting.prevent_conflict :
          !!schedulingPrefs.user_prefs?.booking_check_conflicts,
      allowAddParticipants: meeting.allow_add_participants !== undefined ? meeting.allow_add_participants :
        !!schedulingPrefs.user_prefs?.default_allow_add_participants,
      startDelay: meeting.start_delay_minutes != null ? meeting.start_delay_minutes :
        schedulingPrefs.user_prefs?.default_start_delay_minutes != null ?
          schedulingPrefs.user_prefs?.default_start_delay_minutes : 60,
      files: null,
      recurringTimes: meeting.recurring_times || null,
      selectedCalendar: defaultCalendar,
      selectedColor: meeting.booking_color,
      privateVisibility: meeting.private_visibility ?? false,
      bufferStartMinutes: meeting.buffer_start_minutes || schedulingPrefs.user_prefs?.default_buffer_start_minutes || 0,
      bufferEndMinutes: meeting.buffer_end_minutes || schedulingPrefs.user_prefs?.default_buffer_end_minutes || 0,
      allDaySlots: meeting.all_day_slots || false,
    };
  }, [isPoll, isReusable, leaderMap, meeting.allow_add_participants, meeting.all_day_slots, meeting.auto_merge_slots,
    meeting.booking_calendar,
    meeting.buffer_end_minutes, meeting.buffer_start_minutes, meeting.description, meeting.duration_minutes,
    meeting.hold_calendars, meeting.internal_label, meeting.booking_color,
    meeting.notes, meeting.prevent_conflict, meeting.recurring_times, meeting.start_delay_minutes, meeting.title,
    schedulingPrefs.user_prefs?.booking_check_conflicts, schedulingPrefs.user_prefs?.default_allow_add_participants,
    schedulingPrefs.user_prefs?.default_buffer_end_minutes, schedulingPrefs.user_prefs?.default_buffer_start_minutes,
    schedulingPrefs.user_prefs?.default_duration_minutes, schedulingPrefs.user_prefs?.default_start_delay_minutes,
    meeting.num_expected_participants, meeting.private_visibility, selectedLeaderIds, showTokenInputs,
    templateNameLookup, isDirectEvent, leaderCalendarOptions]);

  const meetingForm = useForm<MeetingSettingFormInput>({ defaultValues });
  const locationsForm = useForm<LocationsFormInput>({ defaultValues: locDefaultValues });
  const { control, handleSubmit: formSubmit, watch, reset, setValue } = meetingForm;

  const { locations } = locationsForm.watch();

  const [recurringTimes, currentCal, watchedMeetingName, allDaySlots] = watch(
    ['recurringTimes', 'selectedCalendar', 'meetingName', 'allDaySlots']
  );

  // For Calendar Event creation, check if we have required data
  const isDirectEventValid = useMemo(() => {
    if (!isDirectEvent) return true; // Not a direct event, so always valid

    const hasTimeSlots = meetingSlots && meetingSlots.length > 0;
    const hasCalendar = currentCal !== undefined && currentCal !== null;
    const hasTitle = watchedMeetingName && watchedMeetingName.trim() !== '';

    return hasTimeSlots && hasCalendar && hasTitle;
  }, [isDirectEvent, meetingSlots, currentCal, watchedMeetingName]);

  const isAnyRecurringDayEnabled = useMemo(() => {
    return recurringTimes?.days.mon.enabled || recurringTimes?.days.tue.enabled || recurringTimes?.days.wed.enabled ||
    recurringTimes?.days.thu.enabled || recurringTimes?.days.fri.enabled || recurringTimes?.days.sat.enabled ||
    recurringTimes?.days.sun.enabled;
  }, [recurringTimes]);

  const locReset = locationsForm.reset;
  useDeepCompareEffect(() => {
    locReset(locDefaultValues);
  }, [locDefaultValues, locReset]);


  // Update calendar when leaders change during Calendar Event creation
  useEffect(() => {
    if (isDirectEvent && selectedLeaderIds.length > 0) {
      const currentCalendar = meetingForm.getValues('selectedCalendar');

      // Check if current calendar is still valid (exists in leaderCalendarOptions)
      const isCurrentCalendarValid = currentCalendar &&
        leaderCalendarOptions.some(cal => cal.id === currentCalendar);

      if (!currentCalendar || !isCurrentCalendarValid) {
        // Find the first available calendar from selected leaders
        const leaderCalendar = leaderCalendarOptions.find(cal =>
          selectedLeaderIds.some(leaderId => cal.leaders.includes(leaderId))
        );
        if (leaderCalendar) {
          setValue('selectedCalendar', leaderCalendar.id, { shouldDirty: true });
        }
      }
    }
  }, [isDirectEvent, selectedLeaderIds, leaderCalendarOptions, setValue, meetingForm]);

  const clearRecurringTimesAvailability = useCallback(() => {
    if (!isSavedMeeting) {
      setValue('recurringTimes', null);
      const excludedMeetingSlots = meetingSlots.filter(slot => slot.is_exclude && slot.meeting === meeting.id);
      if (excludedMeetingSlots.length > 0) {
        onDeleteSlots(excludedMeetingSlots);
      }
      handleSaveRecurringTimes();
    }
  }, [handleSaveRecurringTimes, meeting.id, isSavedMeeting, onDeleteSlots, meetingSlots, setValue]);

  const checkForWarnings = useCallback((duration: number) => {
    if (!meeting.auto_merge_slots) return;

    const slotsLessThanDuration = meetingSlots.some(
      slot => getSlotDuration(slot) < (duration || 0)
    );
    if (slotsLessThanDuration && meeting.id) {
      setShowSlotDurationWarning(slotsLessThanDuration);
    }
  // Jost for currentMeetingSlots
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.id, meeting.auto_merge_slots, JSON.stringify(meetingSlots)]);

  const showNoWarnings = () => {
    setShowNoSlotsWarning(false);
    setShowSlotDurationWarning(false);
  };

  const handleShare = () => {
    onShare();
    showNoWarnings();
    checkForWarnings(meetingForm.getValues().duration);
  };

  const handleCancel = () => {
    setOpenEditModal(false);
    reset(defaultValues);
    onCancel();
  };

  const handleCancelEditModal = () => {
    setOpenEditModal(false);
    reset(defaultValues);
  };

  const handleCloseFillTemplateMenu = useCallback(() => {
    setFillTemplateAnchor(null);
  }, []);

  const handleOpenFillTemplateMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setFillTemplateAnchor(event.currentTarget);
  }, []);

  const handleApplyMeetingTemplate = useCallback((tpl: MeetingTemplate) => {
    const titleVal = showTokenInputs
      ? transformSimpleTemplateToMarkup(tpl.title || '', templateNameLookup)
      : tpl.title || '';
    const descVal = showTokenInputs
      ? transformSimpleTemplateToMarkup(tpl.description || '', templateNameLookup)
      : tpl.description || '';
    setValue('meetingName', titleVal, { shouldDirty: true });
    if (!features?.DISABLE_EVENT_DESCRIPTION) {
      setValue('description', descVal, { shouldDirty: true });
    }
    handleCloseFillTemplateMenu();
  }, [
    features?.DISABLE_EVENT_DESCRIPTION, handleCloseFillTemplateMenu, setValue, showTokenInputs, templateNameLookup,
  ]);

  const handleManageTemplates = useCallback(() => {
    handleCloseFillTemplateMenu();
    router.navigate(`${PAGE_URL.SETTINGS}/preferences/saved-templates`);
  }, [handleCloseFillTemplateMenu]);

  const addQuestionButtonClicked = () => {
    setOpenQuestionModal(true);
    setForceRequired(false);
  };

  const templateAddQuestionButtonClicked = () => {
    setOpenQuestionModal(true);
    setForceRequired(true);
  };

  const handlePollVotesAccept = () => {
    pollUpdateHandlerAccept();
  };

  const handlePollVotesReject = () => {
    setValue("selectExactTimes", false, { shouldDirty: true });
    pollUpdateHandlerReject();
  };

  const executeAddHoldCalendar = (cal: number) => {
    // setValue("executiveHold", true, { shouldDirty: true });
    setValue("executiveHoldCalendars", [cal], { shouldDirty: true });
    setExecutiveHoldAlertOpen(null);
  };

  const selectExactTimes = () => {
    setValue("selectExactTimes", true, { shouldDirty: true });
    setOpenSlotChangeModal(false);
  };

  const saveRecurringAvailability = (newRecurringTimes: RecurringTimes) => {
    setValue("recurringTimes", newRecurringTimes, {shouldDirty: true});
  };

  const expandSection = (section: ExpandedMeetingSettingsSection) => {
    setExpandedSection(section);
    if (section) {
      sectionScrollRefs[section]?.current?.scrollIntoView();
    }
  };

  // TODO: anyway to simplify this or pull it out into util or other file?
  const handleSubmit = useCallback(async (data: MeetingSettingFormInput) => {
    // Check meetingName before submitting since we currently can't set it as required in the form
    if (!data.meetingName || data.meetingName.trim() === '') {
      return;
    }

    showNoWarnings();
    checkForWarnings(data.duration);
    setDisableSelectExactTimes(true);
    setTimeout(() => setDisableSelectExactTimes(false), 2000);
    if (!isSavedMeeting) { // create

      const locs = isDirectEvent ? transformLocations(locationsForm.getValues().locations) : undefined;
      let duration = data.duration;
      if (isDirectEvent && meetingSlots[0]) {
        duration = DateTime.fromISO(meetingSlots[0].end_date)
          .diff(DateTime.fromISO(meetingSlots[0].start_date)).as('minutes');
      }

      await onCreateNewMeeting({
        title: showTokenInputs ? transformMarkupToSimpleTemplate(data.meetingName) : data.meetingName,
        title_booked: null,
        internal_label: isReusable
          ? (data.internalMeetingLabel || transformMarkupToText(data.meetingName))
          : undefined,
        description: showTokenInputs ? transformMarkupToSimpleTemplate(data.description) : data.description,
        description_booked: null,
        notes: data.notes || null,
        duration_minutes: duration,
        num_expected_participants: Math.max(data.attendees, meetingParticipants.length),
        executive_hold: data.executiveHoldCalendars.length > 0,
        hold_calendars: data.executiveHoldCalendars,
        leaders: meeting.leader_info?.map(leader => leader.id) || [],
        create_date: '',
        date_scheduled: null,
        event_start_time: null,
        selected_scheduled_time: null,
        event_id: null,
        calendar_tz: calendarTimezone?.name || null,
        secondary_tz: secondaryTimezones?.map(tz => tz?.name || null) || [],
        copy_tz: null,
        secondary_copy_tz: null,
        status: MeetingStatus.PENDING,
        prevent_conflict: data.preventDoubleBookings,
        is_poll: isPoll,
        errors: [],
        error_count: 0,
        show_voter_names: true,
        allow_add_participants: data.allowAddParticipants,
        rooms: locs?.rooms || meeting.rooms || [],
        location_presets: locs?.location_presets || meeting.location_presets || [],
        questions: meeting.questions || {},
        auto_merge_slots: !data.selectExactTimes,
        is_reusable: isReusable || false,
        enable_public_attendee_list: meeting.enable_public_attendee_list || false,
        allow_reschedule_cancel: schedulingPrefs.user_prefs?.allow_reschedule_cancel || false,
        recurring_times: data.recurringTimes || null,
        priority: MeetingPriority.MEDIUM,
        start_delay_minutes: data.startDelay,
        files: [],
        buffer_start_minutes: data.bufferStartMinutes,
        buffer_end_minutes: data.bufferEndMinutes,
        all_day_slots: data.allDaySlots,
        book_on_create: isDirectEvent ? true : undefined,
        booking_calendar: isDirectEvent ? data.selectedCalendar : undefined,
        private_visibility: data.privateVisibility,
        ...locs,
      },
      data.files || undefined,
      meetingParticipants,
      meetingLeaders,
      );
    } else { // update
      onSubmit({
        id: meeting.id,
        title: showTokenInputs && data.meetingName != null
          ? transformMarkupToSimpleTemplate(data.meetingName)
          : data.meetingName,
        internal_label: isReusable
          ? (data.internalMeetingLabel || transformMarkupToText(data.meetingName))
          : undefined,
        description: showTokenInputs && data.description != null
          ? transformMarkupToSimpleTemplate(data.description)
          : data.description,
        notes: data.notes || null,
        duration_minutes: data.duration || meeting.duration_minutes,
        num_expected_participants: data.attendees,
        executive_hold: data.executiveHoldCalendars.length > 0,
        prevent_conflict: data.preventDoubleBookings,
        hold_calendars: data.executiveHoldCalendars,
        auto_merge_slots: data.allDaySlots
          ? true
          : (data.selectExactTimes == null ? undefined : !data.selectExactTimes),
        start_delay_minutes: data.startDelay || meeting.start_delay_minutes,
        recurring_times: data.recurringTimes || null,
        all_day_slots: data.allDaySlots,
      }, data.files || undefined);
      setValue("files", [], {shouldDirty: false});
    }
    setOpenEditModal(false);
  },
  // Need to use deep compare for secondary timezones
  /* eslint-disable react-hooks/exhaustive-deps */
  [checkForWarnings, meeting.id, meeting.rooms, meeting.location_presets, meeting.enable_public_attendee_list,
    meeting.recurring_times, meeting.duration_minutes, meeting.start_delay_minutes, onCreateNewMeeting, showTokenInputs,
    calendarTimezone?.name, JSON.stringify(secondaryTimezones), isPoll, meeting.questions, isReusable, isDirectEvent,
    schedulingPrefs.user_prefs?.allow_reschedule_cancel, onSubmit, setValue, meeting.is_reusable,
    meetingParticipants, meetingLeaders]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setOpenEditModal(false);
      setIsPoll(meeting.is_poll);
      setIsReusable(meeting.is_reusable);
      setIsDirectEvent(meeting.book_on_create || false);
      expandSection('info');
    }
    // Only reset the form when the meeting changes or the panel opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.id, open]);

  // If a question is removed while token markup is in local form state, strip dangling tokens immediately.
  useEffect(() => {
    if (!open || !showTokenInputs) return;

    const validQuestionIds = new Set(Object.keys(meeting.questions || {}).map(Number));
    const currentMeetingName = meetingForm.getValues('meetingName') || '';
    const currentDescription = meetingForm.getValues('description') || '';
    const cleanedMeetingName = removeDeletedQuestionTokensFromMarkup(currentMeetingName, validQuestionIds);
    const cleanedDescription = removeDeletedQuestionTokensFromMarkup(currentDescription, validQuestionIds);

    if (cleanedMeetingName !== currentMeetingName) {
      setValue('meetingName', cleanedMeetingName, { shouldDirty: true });
    }
    if (cleanedDescription !== currentDescription) {
      setValue('description', cleanedDescription, { shouldDirty: true });
    }
  }, [open, showTokenInputs, meeting.questions, meetingForm, setValue]);

  // Update booking calendar when defaults are fetched for new direct events
  useEffect(() => {
    const isNewDirectEvent = meeting.id === -1 && !!meeting.book_on_create;

    if (meetingDefaults && isNewDirectEvent && open) {
      if (meetingDefaults.booking_calendar?.id !== undefined) {
        setValue('selectedCalendar', meetingDefaults.booking_calendar.id, { shouldDirty: false });
      }
    }
  }, [meetingDefaults, open, meeting.id, meeting.book_on_create, setValue]);

  // TODO: anyway to simplify this?
  useEffect(() => {
    if (meetingType === 'ONE_OFF') {
      setIsPoll(false);
      setIsReusable(false);
      setIsDirectEvent(false);
      setValue('preventDoubleBookings', schedulingPrefs.user_prefs?.booking_check_conflicts || false);
      if (meeting.recurring_times) {
        clearRecurringTimesAvailability();
      }
    } else if (meetingType === 'POLL') {
      setIsPoll(true);
      setIsReusable(false);
      setIsDirectEvent(false);
      setValue('preventDoubleBookings', false);
      const {meetingName} = meetingForm.getValues();
      if (meetingName.includes('{{')) {
        setValue('meetingName', '');
      }
      if (meeting.recurring_times) {
        clearRecurringTimesAvailability();
      }
    } else if (meetingType === 'REUSABLE') {
      setIsReusable(true);
      setIsDirectEvent(false);
      setValue('preventDoubleBookings', schedulingPrefs.user_prefs?.booking_check_conflicts || false);
      setIsPoll(false);
    } else if (meetingType === 'CALENDAR_EVENT') {
      setIsPoll(false);
      setIsReusable(false);
      setIsDirectEvent(true);
      setValue('preventDoubleBookings', schedulingPrefs.user_prefs?.booking_check_conflicts || false);
      if (meeting.recurring_times) {
        clearRecurringTimesAvailability();
      }

      // Ensure calendar dropdown has a default value for calendar events
      const currentCalendar = meetingForm.getValues('selectedCalendar');
      if (!currentCalendar && leaderCalendarOptions.length > 0) {
        // Set the first available calendar as default
        setValue('selectedCalendar', leaderCalendarOptions[0].id, { shouldDirty: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingType]);

  useEffect(() => {
    const {meetingName} = meetingForm.getValues();
    if (isReusable && !meetingName) {
      setValue('meetingName', defaultValues.meetingName, { shouldDirty: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReusable]);

  //when prevent conflicts is changed in share modal, update meeting side panel
  useEffect(() => {
    if (isSavedMeeting) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.prevent_conflict]);

  // the booking calendar may dynamically change when leaders are updated. This updates the form correctly
  useEffect(() => {
    if (meeting.booking_calendar !== undefined) {
      setValue('selectedCalendar', meeting.booking_calendar, { shouldDirty: true });
    }
  }, [meeting.booking_calendar, setValue, meetingForm]);

  // the booking color may dynamically change when calendar is updated. This updates the form correctly
  useEffect(() => {
    if (meeting.booking_color !== undefined) {
      setValue('selectedColor', meeting.booking_color, { shouldDirty: true });
    }
  }, [meeting.booking_color, setValue]);

  useEffect(() => {
    if (meeting.private_visibility !== undefined) {
      setValue('privateVisibility', meeting.private_visibility, { shouldDirty: true });
    }
  }, [meeting.private_visibility, setValue]);

  useEffect(() => {
    if (meetingParticipants.length !== meetingForm.getValues().attendees) {
      setValue('attendees', Math.max(meetingParticipants.length, meetingForm.getValues().attendees));
    }
    // we only care about participants length changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingParticipants.length, setValue]);

  const handleAutoSubmit = useCallback(async (data: MeetingSettingFormInput) => {
    showNoWarnings();
    checkForWarnings(data.duration);
    setDisableSelectExactTimes(true);
    setTimeout(() => setDisableSelectExactTimes(false), 2000);

    await onSubmit({
      id: meeting.id,
      title: showTokenInputs && data.meetingName != null
        ? transformMarkupToSimpleTemplate(data.meetingName)
        : data.meetingName,
      description: showTokenInputs && data.description != null
        ? transformMarkupToSimpleTemplate(data.description)
        : data.description,
      notes: data.notes || null,
      duration_minutes: data.duration || meeting.duration_minutes,
      num_expected_participants: Math.max(data.attendees, meetingParticipants.length),
      executive_hold: data.executiveHoldCalendars && data.executiveHoldCalendars.length > 0,
      hold_calendars: data.executiveHoldCalendars,
      prevent_conflict: data.preventDoubleBookings,
      auto_merge_slots: data.allDaySlots ? true : (data.selectExactTimes == null ? undefined : !data.selectExactTimes),
      start_delay_minutes: data.startDelay || meeting.start_delay_minutes,
      recurring_times: data.recurringTimes,
      booking_calendar: data.selectedCalendar,
      booking_color: data.selectedColor,
      private_visibility: data.privateVisibility,
      all_day_slots: data.allDaySlots,
    }, data.files || undefined);
    setValue("files", [], {shouldDirty: false});
    setOpenEditModal(false);
  }, [checkForWarnings, onSubmit, meeting.id, meeting.duration_minutes, meeting.start_delay_minutes,
    showTokenInputs, setValue, meetingParticipants.length]);

  // selectExactTimes, preventDoubleBookings, recurringTimes need to always be autoSubmit to call updateNewMeeting or
  // updateMeeting because they change slots and slot warnings, duration effects slots as well so must be autosubmit for
  // new meetings. The other fields only require auto when the meeting is saved otherwise they are handled in create.
  const autoSubmitFields: (keyof MeetingSettingFormInput)[] =
  ['executiveHold', 'executiveHoldCalendars', 'selectExactTimes', 'preventDoubleBookings', 'recurringTimes',
    'allDaySlots'];
  if (!isSavedMeeting) {
    autoSubmitFields.push('duration');
  } else {
    autoSubmitFields.push('files');
    autoSubmitFields.push('notes');
    autoSubmitFields.push('selectedCalendar');
    autoSubmitFields.push('selectedColor');
    autoSubmitFields.push('privateVisibility');
  }

  useAutoSubmit({
    control: meetingForm.control,
    memoizedHandleSubmit: handleAutoSubmit,
    fields: autoSubmitFields,
    options: {
      executiveHold: { debounce: 2000 },
      preventDoubleBookings: { debounce: 400 },
      executiveHoldCalendars: { debounce: 2000 },
      selectExactTimes: { debounce: 400 },
      reusableMeeting: { debounce: 400 },
      files: { debounce: 400 },
      notes: { debounce: 800 },
      recurringTimes: {debounce: 400},
      selectedCalendar: {debounce: 400},
      privateVisibility: {debounce: 400},
    },
    handleSubmit: meetingForm.handleSubmit,
    getValues: meetingForm.getValues,
    reset: meetingForm.reset
  });

  if (!open) return null;

  return (
    <>
      <form id="meeting-form" onSubmit={formSubmit(handleSubmit)} style={{ height: '100%' }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: 'flex',
            position: "relative",
            flexDirection: "column",
            paddingLeft: 1.5,
            paddingRight: 1.5
          }}>

          {/* TODO: Pull out Accordion _content_ into separate components */}

          <Stack spacing={1} sx={{
            flex: 1
          }}>
            {((!isPoll || isSavedMeeting) || isPoll) && (
              <>
                {isSavedMeeting && (
                  <Box sx={{ paddingBottom: 1 }}>
                    <Typography
                      variant="h1"
                      sx={{ fontSize: 20, '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                      onClick={() => setOpenEditModal(true)}
                    >
                      <CabTextTokenDisplay templateVars={templateVars}>
                        {meeting.title || ''}
                      </CabTextTokenDisplay>
                    </Typography>
                  </Box>
                )}
                <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'info'}>
                  <StyledAccordionSummary
                    onClick={() => expandSection(expandedSection === 'info' ? null : 'info')}
                    icon={IoInformationCircleOutline}
                    title={isPoll ? 'Poll Information' : 'Meeting Details'}
                    id="panel1a"
                    rightContent={!isSavedMeeting && expandedSection === 'info' ? (
                      <CabButton
                        id="MeetingSettings-fill-from-template"
                        buttonType="text"
                        size="small"
                        aria-controls={fillTemplateAnchor ? 'MeetingSettings-fill-template-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={fillTemplateAnchor ? 'true' : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFillTemplateMenu(e);
                        }}
                        endIcon={<CabIcon Icon={IoChevronDown} sx={{ width: '14px' }} />}
                        sx={{
                          backgroundColor: colors.lavendar100,
                          color: colors.calmGrey700,
                          paddingLeft: 1.25,
                          paddingBottom: 0.75,
                          '&:hover': {
                            backgroundColor: colors.lavendar200,
                          },
                        }}
                      >
                        Templates
                      </CabButton>
                    ) : undefined}
                  />
                  <AccordionDetails>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: 'flex',
                        position: "relative",
                        flexDirection: "column",
                        gap: 2
                      }}>
                      {!isSavedMeeting ? (
                        <>
                          {isReusable && (
                            <DependentController name="internalMeetingLabel" control={control}
                              dependencies={['meetingName']}
                              render={({ field: { ref, ...field }, deps: [meetingName] }) => (
                                <CabTextInput
                                  {...field}
                                  inputRef={ref}
                                  label={'Internal Meeting Label'}
                                  placeholder={transformMarkupToText(meetingName as string)}
                                  multiline
                                  minRows={1}
                                  maxRows={3}
                                  overrides={{ slotProps: { inputLabel: { shrink: true } } }}
                                  formControlSx={{ flex: 1 }}
                                  endIcon={
                                    <CabTooltip
                                      wrapWithSpan
                                      title={'This label helps you organize your reusable meetings. It will not '
                                        + 'be on any events, and your attendees will never see it.'}
                                      sx={{ alignSelf: 'center' }}
                                    >
                                      <CabIcon Icon={IoHelpCircleOutline} sx={{ fontSize: '20px' }} />
                                    </CabTooltip>
                                  }
                                />
                              )} />
                          )}

                          <Controller
                            name="meetingName"
                            control={control}
                            // rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              showTokenInputs ? (
                                <CabTextTokenInput
                                  {...field}
                                  inputRef={ref}
                                  label={'Event Title *'}
                                  id="MeetingSettings-event-title"
                                  // required
                                  multiline
                                  templateVars={templateVars}
                                  inputProps={{ style: { maxHeight: 75, minHeight: 28 } }}
                                  onAddQuestion={(isSavedMeeting && (meeting.is_reusable || meeting.is_poll)) ?
                                    () => templateAddQuestionButtonClicked()
                                    : undefined}
                                />
                              ) : (
                                <CabTextInput
                                  {...field}
                                  inputRef={ref}
                                  label={'Event Title *'}
                                  id="MeetingSettings-event-title"
                                  // required
                                  multiline
                                  minRows={1}
                                  maxRows={3}
                                />
                              )
                            )}
                          />
                          {!features?.DISABLE_EVENT_DESCRIPTION && (
                            <Controller
                              name="description"
                              control={control}
                              render={({ field: { ref, ...field } }) => showTokenInputs ? (
                                <CabTextTokenInput
                                  {...field}
                                  inputRef={ref}
                                  label='Event Description'
                                  id="MeetingSettings-event-description"
                                  multiline
                                  // minRows={3}
                                  // maxRows={6}
                                  templateVars={templateVars}
                                  inputProps={{ style: { height: 75 } }}
                                  onAddQuestion={(isSavedMeeting && (meeting.is_reusable || meeting.is_poll)) ?
                                    () => templateAddQuestionButtonClicked()
                                    : undefined}
                                />
                              ) : (
                                <CabTextInput
                                  {...field}
                                  inputRef={ref}
                                  label='Event Description'
                                  multiline
                                  id="MeetingSettings-event-description"
                                  minRows={3}
                                  maxRows={6}
                                />
                              )}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {meeting.internal_label && (
                            <>
                              <Typography variant="h1" sx={{fontSize: "20px"}}>
                                {meeting.internal_label}
                              </Typography>
                            </>
                          )}
                          {meeting.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 6
                              }}>
                              <CabTextTokenDisplay templateVars={templateVars}>
                                {meeting.description || ''}
                              </CabTextTokenDisplay>
                            </Typography>
                          )}
                        </>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        {!isDirectEvent && (
                          <FormControl sx={{ marginBottom: 0 }}>
                            <StyledFormLabel sx={{ marginBottom: !isSavedMeeting ? 1 : 0 }}>
                              Meeting Length
                            </StyledFormLabel>
                            {!isSavedMeeting ? (
                              <Controller
                                name="duration"
                                control={control}
                                rules={{
                                  required: true,
                                  validate: (value) => value > 0 || 'Duration must be greater than 0'
                                }}
                                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                  allDaySlots ? (
                                    <Typography variant="subtitle1" sx={{ marginTop: 0 }}>
                                      {field.value ? formatDuration(field.value) : formatDuration(0)}
                                    </Typography>
                                  ) : (
                                    <StyledCabDurationPicker
                                      {...field}
                                      durationMinutes={field.value}
                                      sx={{ marginBottom: "12px" }}
                                    />
                                  )
                                )}
                              />
                            ) : (
                              meeting.duration_minutes != null && (
                                <Controller
                                  name="duration"
                                  control={control}
                                  render={({ field: { ref, ...field } }) => (
                                    <Typography variant="subtitle1" sx={{ marginTop: 0, }}>
                                      {field.value ? formatDuration(field.value) : formatDuration(0)}
                                    </Typography>
                                  )}
                                />
                              )
                            )}
                          </FormControl>
                        )}
                        {isPoll && (
                          <FormControl sx={{ marginBottom: 0 }}>
                            <StyledFormLabel sx={{ marginLeft: 0, marginBottom: !isSavedMeeting ? 1 : 0 }}>
                              # Expected Responses
                            </StyledFormLabel>
                            {!isSavedMeeting ? (
                              <Controller name="attendees" control={control}
                                rules={{ required: true }}
                                render={({ field: { ref, ...field }, fieldState: { error } }) => {
                                  return (
                                    <>
                                      <CabTextInput
                                        {...field}
                                        inputRef={ref}
                                        size={"xsmall"}
                                        sx={{
                                          maxWidth: 160,
                                          // hide up down arrows
                                          // Chrome, Safari, Edge, Opera
                                          "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                                            WebkitAppearance: "none",
                                            margin: 0,
                                          },
                                          // Firefox
                                          'input[type=number]': {
                                            MozAppearance: 'textfield',
                                          },
                                        }}
                                        inputProps={{
                                          type: 'number',
                                          sx: { paddingTop: "6px", paddingBottom: "6px", paddingLeft: 1 }
                                        }}
                                      />
                                      {error && (
                                        <Box sx={{
                                          maxWidth: '160px'
                                        }}>
                                          <Alert severity="warning" variant="outlined"
                                            sx={{fontSize: '12px', padding: '4px', marginTop: '4px',
                                              "& .MuiAlert-icon": {
                                                fontSize: 20
                                              },
                                              "& .MuiAlert-message": {
                                                padding: 0
                                              }}}>
                                            {error?.type === 'required' ? 'Expected Responses required'
                                              : error?.type === 'number' ?
                                                `You already have ${meetingParticipants
                                                  .length} attendees listed for this poll`
                                                : error?.type === 'notZero' ?
                                                  '0 is not valid'
                                                  : null}
                                          </Alert>
                                        </Box>
                                      )}
                                    </>
                                  );
                                }}
                              />
                            ) : (
                              <Typography variant="subtitle1">
                                {meeting.num_expected_participants || 1}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      </Box>
                      {isSavedMeeting && (
                        <Box
                          sx={{
                            display: "flex",
                            width: "100%"
                          }}>
                          <CabButton
                            buttonType='secondary'
                            onClick={() => setOpenEditModal(true)}
                            icon={<CabIcon
                              Icon={IoPencilOutline}
                              color={colors.lavendar700}
                              sx={{fontSize: '25px', marginRight: '4px'}}
                            />}
                            sx={{ padding: 1, fontSize: 14, width: '100%', fontWeight: "500" }}
                            size="small"
                          >
                            Edit Meeting Info
                          </CabButton>
                        </Box>
                      )}

                      {/* currently we need to check if selectedCalendar has changed from the undefined value */}
                      {(isSavedMeeting && currentCal !== undefined) || isDirectEvent ? (
                        <>
                          <CalendarAndLocation
                            isOwner={isOwner}
                            meeting={meeting}
                            zoomSettings={zoomSettings}
                            user={user}
                            leaders={leaders}
                            leaderCalendarOptions={leaderCalendarOptions}
                            onAddZoom={onAddZoom}
                            calendars={calendars}
                            meetingRooms={meetingRooms}
                            presetLocations={presetLocations}
                            onOpenQuestionModal={() => setOpenQuestionModal(true)}
                            control={control as unknown as Control}
                            locations={locations}
                            locationsForm={locationsForm}
                            onSubmit={onSubmit}
                            noSelectedLeaders={selectedLeaderIds.length === 0}
                          />
                        </>
                      ) : null}
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                {!isDirectEvent && (
                  <>
                    <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'holds'}>
                      <StyledAccordionSummary
                        onClick={() => expandSection(expandedSection === 'holds' ? null : 'holds')}
                        id="panel1a"
                        title="Calendar Holds"
                        icon={IoBookmarkOutline}
                      />
                      <AccordionDetails>
                        <Typography variant='body2' color={colors.black700}>
                          Add holds to the selected person's connected Google or Microsoft calendars. Holds can be
                          modified or removed at any time.
                        </Typography>
                        <ExecutiveHoldControl
                          control={control}
                          meeting={meeting}
                          isOwner={isOwner}
                          showHolds={showHolds}
                          silenceHolds={schedulingPrefs.user_prefs?.silence_executive_hold_notice || false}
                          openUpgradeModal={openUpgradeModal}
                          setExecutiveHoldAlertOpen={setExecutiveHoldAlertOpen}
                          meetingForm={meetingForm}
                          calendarOptions={holdCalendarOptions}
                          meetingErrors={meetingErrors}
                          onShowErrors={onShowErrors}
                        />
                      </AccordionDetails>
                    </StyledAccordion>
                    <ScrollAnchor ref={holdsScrollRef} />
                  </>
                )}

                <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'notes'}>
                  <StyledAccordionSummary
                    onClick={() => expandSection(expandedSection === 'notes' ? null : 'notes')}
                    id="panel1a"
                    title="Internal Notes"
                    icon={IoDocumentTextOutline}
                  />
                  <AccordionDetails>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <CabTextInput
                          {...field}
                          inputRef={ref}
                          fullWidth
                          maxRows={4}
                          minRows={3}
                          multiline
                          placeholder={"Add a private note for yourself or your backup collaborators to see only " +
                            "in Cabinet."}
                        />
                      )}
                    />
                  </AccordionDetails>
                </StyledAccordion>
                <ScrollAnchor ref={notesScrollRef} />

                <Menu
                  id="MeetingSettings-fill-template-menu"
                  anchorEl={fillTemplateAnchor}
                  open={Boolean(fillTemplateAnchor)}
                  onClose={handleCloseFillTemplateMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    list: { 'aria-labelledby': 'MeetingSettings-fill-from-template' }
                  }}
                >
                  {meetingTemplatesFetching ? (
                    <MenuItem disabled dense>
                      <ListItemText slotProps={{
                        primary: { variant: 'body2' }
                      }}>
                        Loading templates...
                      </ListItemText>
                    </MenuItem>
                  ) : fillTemplateMenuItems.length === 0 ? (
                    <MenuItem disabled dense>
                      <ListItemText slotProps={{
                        primary: { variant: 'body2' }
                      }}>
                        No saved templates
                      </ListItemText>
                    </MenuItem>
                  ) : (
                    fillTemplateMenuItems.map((tpl) => (
                      <MenuItem
                        key={tpl.id}
                        dense
                        onClick={() => handleApplyMeetingTemplate(tpl)}
                      >
                        <ListItemText
                          primary={tpl.name}
                          secondary={tpl.description ? tpl.description.slice(0, 80) +
                            (tpl.description.length > 80 ? '…' : '')
                            : undefined}
                          slotProps={{
                            secondary: { variant: 'caption', sx: {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              maxWidth: 280,
                            } }
                          }}
                        />
                      </MenuItem>
                    ))
                  )}
                  <MenuItem
                    dense
                    sx={{ borderTop: `1px solid ${colors.black200}`, marginTop: 0.5 }}
                    onClick={handleManageTemplates}
                  >
                    <ListItemText primary="Manage Templates" />
                  </MenuItem>
                </Menu>


                <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'times'} >
                  <StyledAccordionSummary
                    onClick={() => expandSection(expandedSection === 'times' ? null : 'times')}
                    id="panel1a"
                    title={isPoll ? 'Poll Times' : 'Meeting Times'}
                    icon={IoCalendarOutline}
                  />
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}>
                      {!isSavedMeeting && (
                        <Typography variant="body2" color={colors.black700} sx={{
                          marginTop: "2px"
                        }}>
                          {dragTimeslotsText}
                        </Typography>
                      )}
                      <Box>
                        {isPoll && (
                          <Controller
                            name="allDaySlots"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                              <CabControlLabel
                                control={(
                                  <CabSwitch
                                    {...field}
                                    checked={value}
                                    onChange={(_, checked) => {
                                      onChange(checked);
                                      if (checked) {
                                        setValue('selectExactTimes', false, { shouldDirty: true });
                                      } else {
                                        const allDayMeetingSlots = meetingSlots.filter((slot) => (
                                          currentMeetingFilter(slot, meeting.id) && !slot.is_exclude
                                          && (!!slot.all_day || !!slot.allDay)
                                        ));
                                        if (allDayMeetingSlots.length > 0) {
                                          onDeleteSlots(allDayMeetingSlots);
                                        }
                                      }
                                    }}
                                  />
                                )}
                                label="Select full day slots"
                                labelPlacement="end"
                              />
                            )}
                          />
                        )}
                        <Controller
                          name="selectExactTimes"
                          control={control}
                          render={({ field: { value, ref, onChange, ...field } }) => (
                            <CabControlLabel
                              control={(
                                <CabSwitch
                                  {...field}
                                  checked={value}
                                  onChange={!value && meetingSlots.length > 0 ?
                                    () => setOpenSlotChangeModal(true)
                                    : (e, checked) => (
                                      onChange(e, checked))
                                  }
                                  disabled={meetingForm.formState.isSubmitting
                                    || slotsAreRecalculating
                                    || disableSelectExactTimes
                                    || allDaySlots}
                                />
                              )}
                              label={(
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5
                                  }}>
                                  Select exact time blocks
                                  <CabTooltip
                                    title={"When you drag times, Cabinet will create individual time options within " +
                                      "each window instead of one large block."}
                                    placement="top"
                                    wrapWithSpan
                                  >
                                    <CabIcon Icon={IoHelpCircleOutline} sx={{ fontSize: 18 }} />
                                  </CabTooltip>
                                </Box>
                              )}
                              labelPlacement="end"
                            />
                          )} />
                      </Box>

                      {meetingSlots.length > 0 && (
                        <Typography variant='h2'>Selected Times</Typography>
                      )}
                      <TimeSlotChips
                        readonly={false}
                        selectedSlots={meetingSlots}
                        currentMeetingId={meeting.id}
                        handleDeleteSlots={onDeleteSlots}
                        timezone={calendarTimezone}
                        control={control}
                        allDaySlots={allDaySlots}
                      />
                      {isReusable && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '8px'
                          }}>
                          <Typography variant='h2'>Recurring Range</Typography>
                          {meeting.recurring_times && !!isAnyRecurringDayEnabled && (
                            <CabButton
                              buttonType='tertiary'
                              size='small'
                              icon={<CabIcon Icon={IoPencilOutline} sx={{fontSize: '16px'}} />}
                              sx={{height: '24px'}}
                              onClick={() => setRecurringRangeModalOpen(true)}
                            >
                              Edit Range
                            </CabButton>
                          )}
                        </Box>
                      )}
                      {isReusable && (
                        !recurringTimes || !isAnyRecurringDayEnabled ? (
                          <CabButton
                            buttonType='secondary'
                            onClick={() => setRecurringRangeModalOpen(true)}
                            icon={<CabIcon
                              Icon={IoAddOutline}
                              color={colors.lavendar700}
                              sx={{fontSize: '25px', marginRight: '4px'}}
                            />}
                            sx={{ padding: 1, fontSize: 14 }}
                          >
                            Add Recurring Range
                          </CabButton>
                        ) : (
                          <Box>
                            <RecurringDayChips
                              recurringTimes={recurringTimes}
                              timezone={calendarTimezone}
                              onSlotClick={() => setRecurringRangeModalOpen(true)}
                            />
                          </Box>
                        )
                      )}
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'participants'}>
                  <StyledAccordionSummary
                    onClick={() => expandSection(expandedSection === 'participants' ? null : 'participants')}
                    icon={IoPeopleOutline}
                    title="Participants"
                    id="panel1a"
                  />
                  <AccordionDetails>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: 'flex',
                        flexDirection: "column",
                        gap: 2
                      }}>
                      <MeetingParticipants
                        leaderInfo={meeting.leader_info || []}
                        additionalParticipants={meetingParticipants}
                        meetingLeaders={meetingLeaders}
                        isEditable={isOwner}
                        onRemoveLeader={onRemoveLeader}
                        handleDeleteAttendee={handleDeleteAttendee}
                        onClickParticipant={() => setEditParticipantsModalOpen(true)}
                      />
                      <CabButton
                        buttonType='secondary'
                        onClick={() => setEditParticipantsModalOpen(true)}
                        icon={<CabIcon
                          Icon={IoPencilOutline}
                          color={colors.lavendar700}
                          sx={{fontSize: '25px', marginRight: '4px'}}
                        />}
                        sx={{ padding: 1, fontSize: 14 }}
                        disabled={!isOwner}
                      >
                        Edit Participants
                      </CabButton>
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                {(features?.MEETING_FILES && hasMicrosoftGrant) && <>
                  <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'files'}>
                    <StyledAccordionSummary
                      onClick={() => expandSection(expandedSection === 'files' ? null : 'files')}
                      id="panel1a"
                      title="Files"
                      icon={IoDocumentOutline}
                    />
                    <AccordionDetails>
                      <Controller
                        name="files"
                        control={control}
                        render={({field: {onChange, value, ...fields}}) => {
                          return (
                            <>
                              <Typography variant='h2'>Add File Attachments</Typography>
                              <Typography variant="body2" color={colors.black700} sx={{
                                marginTop: "2px"
                              }}>
                                {uploadFilesText}
                              </Typography>
                              <Button component="label" color="secondary" variant="contained"
                                sx={{width: "100%", marginTop: 1}}
                              >
                                Add File
                                <input
                                  {...fields}
                                  hidden
                                  type="file"
                                  accept={ACCEPTED_MIME_EXTS.join(",")}
                                  multiple
                                  onChange={(e) => {
                                    const max_total_size = 30 * 1000 * 1000;
                                    const max_file_size = 3 * 1000 * 1000;
                                    const files = Array.from(e.target.files || []);
                                    let size = 0;
                                    const uploadFiles: File[] = [];
                                    let errorExists = false;
                                    for (let i = 0; i < files.length; i++) {
                                      const file = files[i];
                                      if (file.size + size < max_total_size && file.size < max_file_size) {
                                        uploadFiles.push(file);
                                        size += file.size;
                                      } else {
                                        errorExists = true;
                                        break;
                                      }
                                    }
                                    if (!errorExists) {
                                      onChange([...(value || []), ...uploadFiles]);
                                    }
                                    setMaxFilesExceeded(errorExists);
                                  }}
                                />
                              </Button>
                              {maxFilesExceeded &&
                                <Alert severity="error" variant="outlined" sx={{marginTop: 1}}>
                                  Each file must be less than 3MB and a total of 30MB can be uploaded
                                </Alert>
                              }
                              {((meeting.files && meeting.files.length > 0) || (value && value.length > 0)) &&
                                <Box sx={{marginTop: 1}}>
                                  <Typography variant='h2'>
                                    Attached Files
                                  </Typography>
                                  {meeting.files?.map((file) => {
                                    return <CabToggleChip
                                      key={file.id}
                                      label={file.filename}
                                      sx={{marginTop: 0.5, width: "100%", justifyContent: "space-between"}}
                                      onDelete={() => deleteMeetingFile(file.id, meeting.id)}
                                    />;
                                  })}
                                  {/* This is here to show the file name while it's uploading */}
                                  {value?.map((file) => {
                                    return <CabToggleChip
                                      key={file.name}
                                      label={file.name}
                                      sx={{marginTop: 0.5, width: "100%", justifyContent: "space-between"}}
                                      onDelete={() => onChange(value.filter(f => f !== file))}
                                    />;
                                  })}
                                </Box>
                              }
                            </>
                          );
                        }}
                      />
                    </AccordionDetails>
                  </StyledAccordion>
                  <ScrollAnchor ref={filesScrollRef} />
                </>
                }

                {!isDirectEvent && (
                  <>
                    <StyledAccordion elevation={0} disableGutters expanded={expandedSection === 'questions'}>
                      <StyledAccordionSummary
                        onClick={() => expandSection(expandedSection === 'questions' ? null : 'questions')}
                        id="panel1a"
                        title={isPoll ? 'Poll Questions' : 'Meeting Questions'}
                        icon={IoHelpCircleOutline}
                      />
                      <AccordionDetails>
                        {isSavedMeeting && (
                          <Box>
                            <QuestionChips
                              questions={meeting.questions || {}}
                              handleAddQuestion={addQuestionButtonClicked}
                              addButton
                              currentMeetingId={meeting.id}
                              readonly={true}
                              isPoll={isPoll ?? false}
                              meetingFields={{title: meeting.title || '', description: meeting.description,
                                locations: meeting.locations}}
                              hasSingleSelectOption={user?.features.SINGLE_SELECT_QUESTIONS}
                            />
                          </Box>
                        )}

                        {!isSavedMeeting && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <Box
                              id='Meeting-settings-question-container'
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                              }}>
                              <Typography variant="body2" color={colors.black700}>
                                {isPoll ? 'Add questions for your poll participants to answer.'
                                  : 'Add questions for your meeting respondents to answer.'}
                              </Typography>
                              <QuestionChips
                                questions={meeting.questions || {}}
                                handleAddQuestion={addQuestionButtonClicked}
                                addButton
                                currentMeetingId={meeting.id}
                                readonly={true}
                                isPoll={isPoll ?? false}
                                hasSingleSelectOption={user?.features.SINGLE_SELECT_QUESTIONS}
                              />
                            </Box>
                          </Box>
                        )}

                      </AccordionDetails>
                    </StyledAccordion>
                    <ScrollAnchor ref={questionsScrollRef} />
                  </>
                )}

              </>
            )}
          </Stack>

          {/* TODO: Make each modal/dialog section its own component.
          Maybe multiple exports from separate file, e.g. MeetingModals? */}

          <DialogActionsStyled>
            {!isSavedMeeting ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: 'column',
                  width: "100%"
                }}>
                {/* Show field errors here after first submit */}
                {(() => {
                  if (meetingForm.formState.submitCount == 0) return null;
                  const errors: string[] = [];
                  if (!watchedMeetingName || watchedMeetingName.trim() === '') {
                    errors.push('Event Title is required');
                  }
                  Object.entries(meetingForm.formState.errors).forEach(([field, error]) => {
                    if (error) {
                      const fieldName = field === 'attendees' ? 'Expected Responses' : field;
                      errors.push(error.message || `${fieldName} is required`);
                    }
                  });
                  return errors.length > 0 ? (
                    <Box sx={{ marginBottom: 1, width: '100%' }}>
                      <Alert severity="error" variant="outlined" sx={{ fontSize: '12px', padding: '8px' }}>
                        {errors.map((error, index) => (
                          <Box key={index}>{error}</Box>
                        ))}
                      </Alert>
                    </Box>
                  ) : null;
                })()}
                <Box
                  sx={{
                    display: "flex",
                    width: "100%"
                  }}>
                  <CabButton type="submit" buttonType='primary' size="large"
                    id='MeetingSettings-save-meeting-btn'
                    disabled={meetingForm.formState.isSubmitting || !isDirectEventValid}
                    sx={{flex: 1, marginLeft: 1, minWidth: '55%'}}
                  >
                    {meetingForm.formState.isSubmitting
                      ? <CabSpinner scale={1} sx={{ width: 32 }} />
                      : isDirectEvent ? 'Send Invitation' : (isPoll ? 'Save Poll' : 'Save Meeting')}
                  </CabButton>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                display: "flex", flexDirection: "column", width: "100%", gap: 1,
              }}>
                <CabButton buttonType='secondary' size="large" onClick={handleCancel}
                  sx={{ flex: 1, backgroundColor: colors.white900 }}
                >
                  Close
                </CabButton>
                <CabButton buttonType='primary' size="large" onClick={handleShare}
                  id='MeetingSettings-share-meeting-btn' sx={{ flex: 1 }}
                >
                  Share
                </CabButton>
              </Box>
            )}
          </DialogActionsStyled>

          {loading && (
            <Box
              sx={{
                position: "absolute",
                height: "100%",
                width: "100%",
                backgroundColor: 'rgba(0,0,0,0.5)'
              }}>
              <CabSpinner scale={2} sx={{ marginTop: '50%' }} />
            </Box>
          )}
        </Box>
      </form>
      <CabModal
        open={showNoSlotsWarning || showSlotDurationWarning}
        onClose={() => showNoWarnings()}
        title='Select Time Slots'
        isAlert
        closeOnBackdropClick
        actionButtons={
          <>
            <CabButton buttonType="tertiary" color='primary' onClick={() => showNoWarnings()}>
              {showNoSlotsWarning || showSlotDurationWarning ? 'OK' : 'Cancel'}
            </CabButton>
          </>
        }
        sx={{ zIndex: 1305 }}
      >
        {showNoSlotsWarning && (
          <DialogContentText>
            You must select at least one time slot.
          </DialogContentText>
        )}

        {showSlotDurationWarning && (
          !isPoll
            ? <DialogContentText>
              Some of your selected slots are shorter than the duration of your meeting,
              so they will not be shown to attendees.
            </DialogContentText>
            : <Box>
              <DialogContentText>
                Some slots have a duration shorter than your meeting length
              </DialogContentText>
            </Box>
        )}
      </CabModal>
      <CabModal
        open={openSlotChangeModal}
        onClose={() => setOpenSlotChangeModal(false)}
        title='Slot Change Warning'
        isAlert
        closeOnBackdropClick
        text="Changing this setting may cause your selected slots to change."
        actionButtons={
          <>
            <CabButton buttonType="secondary" onClick={() => setOpenSlotChangeModal(false)}>
              Cancel
            </CabButton>
            <CabButton onClick={() => selectExactTimes()}>
              OK
            </CabButton>
          </>
        }
      />
      {executiveHoldAlertOpen != null && (
        <ExecutiveHoldAlert
          calId={executiveHoldAlertOpen}
          onClose={() => setExecutiveHoldAlertOpen(null)}
          executeAddHoldCalendar={executeAddHoldCalendar}
          updateExecHoldNoticePref={updateExecHoldNoticePref}
          silence_executive_hold_notice={schedulingPrefs?.user_prefs?.silence_executive_hold_notice || false}
        />
      )}
      <EditMeetingInfoModal
        open={openEditModal}
        onCancel={handleCancelEditModal}
        defaultValues={{
          meetingName: watch('meetingName'),
          internalMeetingLabel: watch('internalMeetingLabel'),
          description: watch('description'),
          duration: watch('duration'),
          attendees: watch('attendees'),
        }}
        isPoll={isPoll || false}
        onSubmit={async (data) => {
          setValue('meetingName', data.meetingName);
          setValue('internalMeetingLabel', data.internalMeetingLabel);
          setValue('description', data.description);
          setValue('duration', data.duration);
          setValue('attendees', data.attendees);
          await formSubmit(handleSubmit)();
        }}
        showTokenInputs={showTokenInputs}
        meeting={meeting}
        templateVars={templateVars}
        templateAddQuestionButtonClicked={templateAddQuestionButtonClicked}
        descriptionDisabled={features?.DISABLE_EVENT_DESCRIPTION || false}
        // attendeeValidation={attendeeValidation}
      />
      {openQuestionModal && (
        <QuestionModal
          open={openQuestionModal}
          questions={meeting.questions || {}}
          forceRequired={forceRequired ?? undefined}
          onClose={() => setOpenQuestionModal(false)}
          currentMeetingId={meeting.id}
          isPoll={isPoll ?? false}
          hasSingleSelectOption={user?.features.SINGLE_SELECT_QUESTIONS}
        />
      )}
      <PollUpdateModal
        open={pollUpdateOpen}
        pollUpdateHandlerAccept={handlePollVotesAccept}
        pollUpdateHandlerReject={handlePollVotesReject}
        invalidMeetingSlots={invalidMeetingSlots}
        timezone={calendarTimezone}
      />
      {recurringRangeModalOpen && (
        <RecurringRangeModal
          open={recurringRangeModalOpen}
          timezone={calendarTimezone}
          onClose={() => setRecurringRangeModalOpen(false)}
          onSave={(newRecurringTimes) => saveRecurringAvailability(newRecurringTimes)}
          recurringTimes={recurringTimes || undefined}
          excludedMeetingSlots={meetingSlots.filter(slot => slot.is_exclude)}
          meetingId={meeting.id}
          onDeleteSlots={onDeleteSlots}
          onEditSlot={onEditSlot}
          onExcludedSlotsCreated={onExcludedSlotsCreated}
          onCalendarTimezoneSelected={onCalendarTimezoneSelected}
        />
      )}
      {editParticipantsModalOpen && (
        <EditParticipantsModal
          open={editParticipantsModalOpen}
          onClose={() => setEditParticipantsModalOpen(false)}
          meetingId={meeting.id}
        />
      )}
    </>
  );
};

const DialogActionsStyled = styled(
  DialogActions, { label: "DialogActionsStyled" }
)<CabComponentProps<DialogActionsProps>>(() => ({
  padding: 0,
  paddingTop: 6,
  marginBottom: 4,
  paddingBottom: 8,
  position: 'sticky',
  bottom: 0,
  backgroundColor: meetingPanelGray,
}));

const StyledAccordion = styled(Accordion, { label: 'StyledAccordion' })(() => ({
  border: `1px solid ${colors.black200}`,
  '&:before': { display: 'none' },
  '&.MuiAccordion-root': { borderRadius: 12 },
}));

const StyledAccordionSummary = ({
  onClick, icon, title, id, rightContent
}: { onClick: () => void, icon: IconType, title: string, id: string, rightContent?: ReactNode }) => (
  <AccordionSummary
    expandIcon={<CabIcon Icon={IoChevronDown} />}
    onClick={onClick}
    aria-controls={`${id}-content`}
    id={`${id}-header`}
  >
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        marginRight: 1
      }}>
      <Typography
        variant="h2"
        color={colors.calmGrey700}
        sx={{
          display: 'flex',
          fontSize: 16,
          gap: "5px",
          alignItems: 'center'
        }}>
        <CabIcon Icon={icon} color={colors.calmGrey400} sx={{ fontSize: '20px' }} />
        {title}
      </Typography>
      {rightContent && <Box sx={{
        marginLeft: 3
      }}>{rightContent}</Box>}
    </Box>
  </AccordionSummary>
);

const StyledFormLabel = styled(FormLabel, { label: 'StyledFormLabel' })(() => ({
  fontSize: 12, fontWeight: 600, color: `${colors.calmGrey500} !important`
}));

const ScrollAnchor = styled('div', { label: 'ScrollAnchor' })({
  margin: '0px !important',
  padding: '0px !important',
  height: '0px !important',
});


type MeetingSettingsViewProps = Omit<MeetingSettingsProps, 'meeting'|'meetingType'>
& { meeting: MeetingSettingsProps['meeting'] | null };

const MeetingSettingsView = (props: MeetingSettingsViewProps): ReactElement | null => {
  const [meetingType, setMeetingType] = useState<keyof typeof MEETING_TYPE>('ONE_OFF');

  const isSavedMeeting = props.meeting && props.meeting.id !== -1;
  const isDirectEvent = props.meeting && 'book_on_create' in props.meeting && props.meeting.book_on_create;

  useEffect(() => {
    if (props.open) {
      if (isDirectEvent) {
        setMeetingType("CALENDAR_EVENT");
      } else if (props.meeting?.is_poll) {
        setMeetingType("POLL");
      } else if (props.meeting?.is_reusable) {
        setMeetingType("REUSABLE");
      } else {
        setMeetingType("ONE_OFF");
      }
    } else {
      setMeetingType("ONE_OFF");
    }
  }, [props.meeting?.is_poll, props.meeting?.is_reusable, props.open, isDirectEvent]);

  return (
    <CabPanel
      open={props.open}
      onClose={props.onCancel}
      anchor="right"
      sx={{ '& .MuiDrawer-paper': { backgroundColor: meetingPanelGray } }}
      headerBackgroundColor={meetingPanelGray}
      titleTextColor={colors.calmGrey400}
      title={isSavedMeeting ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
          <CabIcon
            Icon={props.meeting?.is_poll ? IoCheckboxOutline
              : props.meeting?.is_reusable ? IoRepeatOutline
                : isDirectEvent ? IoCalendarNumberOutline : IoMdCalendar}
            size="small"
            color={colors.calmGrey400}
          />
          {props.meeting?.is_poll ? 'Meeting Poll'
            : props.meeting?.is_reusable ? 'Reusable Meeting' : 'One-off Meeting'}
        </Box>
      ) : (
        <CabDropdown<keyof typeof MEETING_TYPE>
          pretty
          onChange={(e) => {
            const newType = e.target.value as keyof typeof MEETING_TYPE;
            setMeetingType(newType);
            if (newType === 'CALENDAR_EVENT') {
              props.onSubmit({ id: -1, book_on_create: true });
            } else {
              props.onSubmit({ id: -1, book_on_create: false });
            }
          }}
          value={meetingType}
          // size='small'
          options={Object.keys(MEETING_TYPE)
            .filter(key => key !== 'CALENDAR_EVENT' || props.user?.features?.CALENDAR_EVENT_EDITING)
            .map((interval) => ({
              value: interval as keyof typeof MEETING_TYPE,
              label: (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "center"
                  }}>
                  {MEETING_TYPE[interval as keyof typeof MEETING_TYPE]}
                  {(interval === "POLL" && !props.user?.features?.MEETING_POLLS) && <CabIcon
                    sx={{marginLeft: 1}}
                    Icon={IoLockClosed}
                  />}
                </Box>
              ),
              IconComponent: interval === 'POLL' ? <CabIcon Icon={IoCheckboxOutline} size="small" />
                : interval === 'REUSABLE' ? <CabIcon Icon={IoRepeatOutline} size="small" />
                  : interval === 'CALENDAR_EVENT' ? <CabIcon Icon={IoCalendarNumberOutline} size="small" />
                    : <CabIcon Icon={IoMdCalendar} size="small" />,

              disabled: (interval === "POLL" && !props.user?.features?.MEETING_POLLS)
            }))}
          sx={{ width: '100%', fontSize: '16px', fontWeight: 700, height: '26px', marginBottom: 1, marginTop: 1,
            '& .MuiInputBase-input': {
              lineHeight: '20px',
            }
          }}
        />
      )}
      closeIcon
    >
      {props.meeting
        ? <MeetingSettings {...props} meeting={props.meeting} meetingType={meetingType} />
        : null}
    </CabPanel>
  );
};


export default MeetingSettingsView;
